import services from "../services";

// Loads every task in a single board column, across all pages, so Prev/Next
// navigation and the "X of Y" indicator reflect the true filtered list rather
// than just the page(s) already scrolled into view on the board.
const MAX_PAGES = 20; // safety cap (20 * PAGE_SIZE tasks per column)
const PAGE_SIZE = 50;
const CACHE_PREFIX = "ticketNavCache:";

// Session-scoped cache so a hard reload / back-forward navigation of the
// detail page (which loses react-router location.state) doesn't force the
// heavy getAllTask(boardId) call to run again for every Prev/Next hop.
export const getNavCacheKey = ({ boardId, columnId, filters = [], activeButton, sortby, sortOrder } = {}) => {
  if (!boardId || !columnId) return null;
  const filterKey = (filters || [])
    .map((f) => `${f.field || ""}:${Array.isArray(f.value) ? f.value.map((v) => v.value || v.label || v).join(",") : f.value || ""}`)
    .join("|");
  const sortKey = activeButton === "table" ? `${sortby || ""}:${sortOrder || ""}` : "";
  return `${CACHE_PREFIX}${boardId}:${columnId}:${filterKey}:${sortKey}`;
};

export const readCachedNavList = (cacheKey) => {
  if (!cacheKey) return null;
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

export const writeCachedNavList = (cacheKey, tasks) => {
  if (!cacheKey) return;
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(tasks));
  } catch (error) {
    // sessionStorage full/unavailable — cache is best-effort, safe to skip.
  }
};

export const buildColumnNavList = async ({
  boardId,
  columnId,
  filters = [],
  activeButton,
  sortby,
  sortOrder,
}) => {
  if (!boardId || !columnId) return [];

  let filterParams = {};
  (filters || []).forEach((f) => {
    if (!f.field || !f.value) return;
    let resolvedValue = f.value;
    if (f.field === "user.name" && Array.isArray(resolvedValue)) {
      const names = resolvedValue.map((u) => u.label).filter(Boolean);
      if (names.length > 0) {
        filterParams = { ...filterParams, field: f.field, value: names.join(",") };
      }
    } else if (f.field === "customProperty" && Array.isArray(resolvedValue)) {
      const ids = resolvedValue.map((p) => p.value).filter(Boolean);
      if (ids.length > 0) {
        filterParams = { ...filterParams, field: f.field, value: ids.join(",") };
      }
    } else if (typeof resolvedValue === "string" && resolvedValue.trim() !== "") {
      filterParams = { ...filterParams, field: f.field, value: resolvedValue };
    }
  });

  const fetchPage = (page) =>
    services.task.GET_TASK({
      page,
      limit: PAGE_SIZE,
      boardId,
      ...filterParams,
      ...(activeButton === "table" && {
        sortBy: sortby,
        sortOrder,
        query: "table",
      }),
    });

  const columnTasksOf = (response) => {
    const data = response?.data?.data || {};
    return Array.isArray(data) ? [] : data[columnId] || [];
  };

  const pages = [];

  let firstResponse;
  try {
    firstResponse = await fetchPage(1);
  } catch (error) {
    console.error("Error building ticket nav list:", error);
    return [];
  }
  pages[0] = columnTasksOf(firstResponse);

  const totalPages = Math.min(Number(firstResponse?.data?.totalPages) || 1, MAX_PAGES);

  if (pages[0].length > 0 && totalPages > 1) {
    // Page 1 tells us how many pages exist — fetch the rest together instead
    // of waiting on them one request at a time.
    const remaining = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) => i + 2).map((page) =>
        fetchPage(page)
          .then(columnTasksOf)
          .catch((error) => {
            console.error("Error building ticket nav list:", error);
            return [];
          })
      )
    );
    remaining.forEach((columnTasks, i) => {
      pages[i + 1] = columnTasks;
    });
  }

  const tasks = [];
  const seenIds = new Set();
  pages.forEach((columnTasks) => {
    (columnTasks || []).forEach((task) => {
      if (task?.id && !seenIds.has(task.id)) {
        seenIds.add(task.id);
        tasks.push(task);
      }
    });
  });

  writeCachedNavList(getNavCacheKey({ boardId, columnId, filters, activeButton, sortby, sortOrder }), tasks);

  return tasks;
};
